import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { logWarn } from "@/lib/log";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = user

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges the session_id first, so skip the /me probe.
    if (window.location.hash?.includes("session_id=")) return;
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const login = async (email, password, remember = false) => {
    const { data } = await api.post("/auth/login", { email, password, remember });
    setUser(data);
    return data;
  };

  const loginWithGoogle = useCallback(async (sessionId) => {
    try {
      const { data } = await api.post("/auth/google/session", { session_id: sessionId, remember: true });
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      throw e;
    }
  }, []);

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name, lang: localStorage.getItem("lang") || "id" });
    if (data && data.id) setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { logWarn("Logout request failed (clearing session anyway):", e); }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, loginWithGoogle, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
